// ==================== DROPDOWN COMPONENT ====================

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '../../utils/helpers';

// ==================== TYPES ====================

export interface DropdownItem {
  /** Unique key for the item */
  key: string;
  /** Label text to display */
  label: string;
  /** Icon component */
  icon?: React.ReactNode;
  /** Custom text color class */
  colorClass?: string;
  /** Whether to show this item */
  visible?: boolean;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick: () => void;
}

export interface DropdownProps {
  /** Menu items to display */
  items: DropdownItem[];
  /** Custom trigger element (defaults to MoreHorizontal icon button) */
  trigger?: React.ReactNode;
  /** Trigger button className */
  triggerClassName?: string;
  /** Menu width */
  menuWidth?: string | number;
  /** Align menu to left or right of trigger */
  align?: 'left' | 'right';
  /** Custom className for the menu */
  menuClassName?: string;
  /** Disabled state */
  disabled?: boolean;
}

// ==================== DROPDOWN MENU COMPONENT ====================

interface DropdownMenuProps {
  items: DropdownItem[];
  position: { top: number; left: number };
  menuWidth: string | number;
  menuClassName?: string;
  onClose: () => void;
}

const DropdownMenu = ({ items, position, menuWidth, menuClassName, onClose }: DropdownMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Adjust position if menu goes off screen
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let newTop = position.top;
      let newLeft = position.left;

      // Check if menu goes below viewport
      if (rect.bottom > viewportHeight - 10) {
        newTop = position.top - rect.height - 40; // Position above trigger
      }

      // Check if menu goes off right edge
      if (rect.right > viewportWidth - 10) {
        newLeft = viewportWidth - rect.width - 10;
      }

      // Check if menu goes off left edge
      if (rect.left < 10) {
        newLeft = 10;
      }

      setAdjustedPosition({ top: newTop, left: newLeft });
    }
  }, [position]);

  const visibleItems = items.filter(item => item.visible !== false);

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9998]" 
        onClick={onClose}
      />
      
      {/* Menu */}
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95, y: -8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -8 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          top: adjustedPosition.top,
          left: adjustedPosition.left,
          width: typeof menuWidth === 'number' ? `${menuWidth}px` : menuWidth,
          zIndex: 9999,
        }}
        className={cn(
          'py-1 bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-lg shadow-2xl',
          menuClassName
        )}
      >
        {visibleItems.map((item) => (
          <button
            key={item.key}
            onClick={() => {
              if (!item.disabled) {
                item.onClick();
                onClose();
              }
            }}
            disabled={item.disabled}
            className={cn(
              'flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors',
              item.disabled 
                ? 'opacity-50 cursor-not-allowed text-(--color-muted-gray)' 
                : 'hover:bg-white/5',
              item.colorClass || 'text-(--color-soft-gray) hover:text-white'
            )}
          >
            {item.icon && <span className="shrink-0">{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        ))}

        {visibleItems.length === 0 && (
          <div className="px-4 py-3 text-sm text-(--color-muted-gray) text-center">
            No actions available
          </div>
        )}
      </motion.div>
    </>,
    document.body
  );
};

// ==================== MAIN DROPDOWN COMPONENT ====================

const Dropdown = ({
  items,
  trigger,
  triggerClassName,
  menuWidth = 192,
  align = 'right',
  menuClassName,
  disabled = false,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  const calculatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = typeof menuWidth === 'number' ? menuWidth : 192;
      
      const top = rect.bottom + 4;
      const left = align === 'right' 
        ? rect.right - width 
        : rect.left;

      setMenuPosition({ top, left });
    }
  }, [menuWidth, align]);

  const handleToggle = () => {
    if (!disabled) {
      calculatePosition();
      setIsOpen(!isOpen);
    }
  };

  // Recalculate position on scroll or resize
  useEffect(() => {
    if (isOpen) {
      const handleReposition = () => calculatePosition();
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
      
      return () => {
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }
  }, [isOpen, calculatePosition]);

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          'p-2 rounded-lg text-(--color-muted-gray) hover:text-white hover:bg-white/5 transition-colors',
          disabled && 'opacity-50 cursor-not-allowed',
          triggerClassName
        )}
      >
        {trigger || <MoreHorizontal size={18} />}
      </button>

      <AnimatePresence>
        {isOpen && (
          <DropdownMenu
            items={items}
            position={menuPosition}
            menuWidth={menuWidth}
            menuClassName={menuClassName}
            onClose={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export { Dropdown };

